import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import NavBar from '../../components/NavBar/NavBar';
import Footer from '../../components/Footer/Footer';
import { client } from "../../lib/client";
import { urlFor } from '../../util/util';

const AuthorProfile = () => {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAuthorData = async () => {
      try {
        setIsLoading(true);
        // Fetch author details
        const authorQuery = `*[_type == "author" && slug.current == $slug][0]{
          _id,
          name,
          slug,
          image,
          jobTitle,
          bio
        }`;
        
        // Fetch latest post by this author
        const postsQuery = `*[_type == "post" && author._ref == $authorId] | order(publishedAt desc)[0...1] {
          _id,
          title,
          slug,
          mainImage,
          publishedAt,
          excerpt,
          "authorName": author->name,
          "authorSlug": author->slug.current
        }`;

        const [authorData] = await Promise.all([
          client.fetch(authorQuery, { slug })
        ]);

        if (authorData) {
          setAuthor(authorData);
          // Only fetch posts if author exists
          const authorPosts = await client.fetch(postsQuery, { authorId: authorData._id });
          setPosts(authorPosts || []);
        }
      } catch (error) {
        console.error('Error fetching author data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchAuthorData();
    }
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Author not found</h1>
            <Link 
              to="/blog" 
              className="text-blue-600 hover:underline"
            >
              ← Back to blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Author Header */}
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                {author.image?.asset && (
                  <img
                    className="h-32 w-32 rounded-full object-cover border-4 border-white shadow-lg"
                    src={urlFor(author.image).width(300).height(300).url()}
                    alt={author.name}
                  />
                )}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{author.name}</h1>
              {author.jobTitle && (
                <p className="text-lg text-blue-600 font-medium">{author.jobTitle}</p>
              )}
              
              {/* Bio */}
              {author.bio && (
                <div className="mt-6 text-gray-600 max-w-2xl mx-auto">
                  <PortableText value={author.bio} />
                </div>
              )}
            </div>

            {/* Author's Posts */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Latest Articles</h2>
              <div className="space-y-8">
                {posts.length > 0 ? (
                  posts.map((post) => (
                    <article key={post._id} className="border-b border-gray-200 pb-8">
                      <Link 
                        to={`/blog/${post.slug.current}`}
                        className="block group"
                      >
                        <div className="flex flex-col md:flex-row gap-6">
                          {post.mainImage?.asset && (
                            <div className="md:w-1/3">
                              <img
                                src={urlFor(post.mainImage).width(600).height(400).url()}
                                alt={post.mainImage.alt || post.title}
                                className="w-full h-48 object-cover rounded-lg"
                              />
                            </div>
                          )}
                          <div className="md:w-2/3">
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {post.title}
                            </h3>
                            {post.excerpt && (
                              <p className="mt-2 text-gray-600">
                                {post.excerpt}
                              </p>
                            )}
                            <div className="mt-3 text-sm text-gray-500">
                              {new Date(post.publishedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))
                ) : (
                  <p className="text-gray-500">No articles found.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AuthorProfile;
